import React, { AriaRole, ForwardedRef, forwardRef, useContext } from 'react';
import clsx from 'clsx';
import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { filterDOMProps, mergeProps, useObjectRef } from '@react-aria/utils';
import { ListState } from '@react-stately/list';
import { AriaLabelingProps, Node } from '@react-types/shared';
import {
  AriaGridListProps,
  useGridList,
  useGridListItem,
  useGridListSelectionCheckbox,
} from '@react-aria/gridlist';
import styles from '@danske/sapphire-css/components/list/list.module.css';

import { Checkbox } from '../../Checkbox';
import { List, ListItem, ListItemText } from '../../List';
import { useListContext } from '../../List/src/ListContext';
import { RadioBox } from '../../Radio/src/RadioBox';
import { SapphireStyleProps, useThemeCheck } from '../../utils';
import { useListWithLoadingStateLayout } from '../../utils/virtualization/useListWithLoadingStateLayout';
import { VirtualizerWithLoadingSkeleton } from '../../utils/virtualization/VirtualizerWithLoadingSkeleton';
import { Skeleton } from '../../Skeleton';
import patchSelectionManager, {
  WithResolveSelectAllToKeysKeys,
} from '../../utils/patchSelectionManager';
import {
  BaseComponentKeys,
  GlobalDomAttributes,
  MultipleSelectionKeys,
} from '../../utils/types';
import { useListState } from './useListState';

export type InteractiveListProps<T> = WithResolveSelectAllToKeysKeys<
  Pick<
    AriaGridListProps<T>,
    | BaseComponentKeys
    | MultipleSelectionKeys
    | 'selectionMode'
    | 'items'
    | 'children'
    | 'onAction'
    | 'autoFocus'
  >
> &
  SapphireStyleProps &
  GlobalDomAttributes & {
    /**
     * Sets HTML tag of outermost element.
     * @default "ul"
     * @deprecated was not used
     */
    elementType?: 'ul' | 'ol';
    /**
     * Removes the divider after the last list item.
     * Useful when rendered in a container which already has borders.
     */
    hideLastDivider?: boolean;
    /** Loading state type */
    loadingState?: 'loadingMore' | 'loading';
    /**
     * Callback called when the user scrolled to the bottom of the list.
     * Useful when implementing infinite scroll.
     */
    onLoadMore?: () => void;
    /**
     * How should the loading skeleton row look like.
     */
    renderLoadingSkeleton?: (index: number) => React.ReactNode;
    /**
     * How many loading rows should be shown when "loadingState" is used.
     * @default 3
     */
    loadingSkeletonRowsCount?: number;
    /**
     * Add custom sized padding to the sides of the list items.
     *
     * @example
     * ```tsx
     * <InteractiveList
     *    sidePadding={tokens.size.spacingContainerHorizontalM.value} hasNegativeSideMargin>
     * </InteractiveList>
     * ```
     */
    sidePadding?: number | string;
    /**
     * Counter acts the side padding, so that list is shifted to the left with
     * same amount and having side padding * 2 added to width – resulting in full
     * width list.
     * Useful in scenarions where list is inside a padded container and desired
     * layout has list going from edge to edge horizontally.
     *
     * @example
     * ```tsx
     * <InteractiveList hasNegativeSideMargin>...</InteractiveList>
     * ```
     */
    hasNegativeSideMargin?: boolean;
    /**
     * Whether interaction with the table is disabled or not.
     * @default false
     */
    isDisabled?: boolean;
  };

const GridListContext = React.createContext<{
  state: ListState<unknown>;
} | null>(null);

const _InteractiveList = <T extends object>(
  {
    onSelectionChange,
    resolveSelectAllToKeys,
    ...props
  }: InteractiveListProps<T>,
  ref: ForwardedRef<HTMLOListElement>
) => {
  useThemeCheck();

  /**
   * Developer Note:
   *
   * We are using `suppressTextValueWarning` because we don't allow children in
   * an `InteractiveListItem` but use some custom props.
   *
   * This means that users will see a warning to provide a `textValue` to each
   * item for the typeahead feature to work in interactive lists.
   * We considered this to be a little agressive so we supress the warning.
   *
   */
  const {
    loadingState,
    loadingSkeletonRowsCount = 3,
    renderLoadingSkeleton = renderDefaultLoadingSkeleton,
    onLoadMore,
    isDisabled,
    disabledKeys,
    ...otherProps
  } = props;

  const state = useListState({
    ...props,
    // @ts-expect-error -- due to the SapphireSelectionManager, the type of onSelectionChange is different, sometimes it only returns Set<Keys> apart from the original "Selection" type. We decided to own this type error, instead of passing it down to the user.
    onSelectionChange,
    disabledKeys,
    suppressTextValueWarning: true,
  });

  patchSelectionManager(state.selectionManager, {
    resolveSelectAllToKeys,
  });
  const listRef = useObjectRef(ref);

  const layout = useListWithLoadingStateLayout({
    loadingState,
    loadingSkeletonRowsCount,
  });

  const { gridProps } = useGridList(
    {
      ...props,
      isVirtualized: true,
      layoutDelegate: layout,
    },
    state,
    listRef
  );

  return (
    <List
      {...mergeProps(otherProps, gridProps)}
      ref={listRef}
      elementType="div"
      aria-busy={loadingState && true}
    >
      <GridListContext.Provider value={{ state }}>
        <VirtualizerWithLoadingSkeleton
          renderLoadingSkeleton={renderLoadingSkeleton}
          loadingSkeletonRowsCount={loadingSkeletonRowsCount}
          onLoadMore={onLoadMore}
          scrollDirection="vertical"
          layout={layout}
          collection={state.collection}
          style={
            {
              height: '100%',
            } /* if the list has a height, we want the virtualizer to know about it */
          }
        >
          {(kind, item) => {
            return (
              kind === 'item' && <GridListItem key={item.key} item={item} />
            );
          }}
        </VirtualizerWithLoadingSkeleton>
      </GridListContext.Provider>
    </List>
  );
};

const GridListItem = forwardRef(function GridListItem<T>(
  {
    item,
    ...otherProps
  }: AriaLabelingProps & {
    item: Node<T>;
    role?: AriaRole;
  },
  ref: ForwardedRef<HTMLDivElement>
) {
  const listContext = useContext(GridListContext);
  if (listContext === null) {
    return null;
  }
  const { state } = listContext;
  const itemRef = useObjectRef<HTMLDivElement | null>(ref);
  const { rowProps, gridCellProps, isDisabled, hasAction, allowsSelection } =
    useGridListItem({ node: item }, state, itemRef);
  const { sidePadding } = useListContext();
  const { focusProps, isFocusVisible } = useFocusRing();
  const { hoverProps, isHovered } = useHover({
    isDisabled: isDisabled || (!hasAction && !allowsSelection),
  });

  return (
    <div
      className={styles['sapphire-list__item']}
      ref={itemRef}
      {...filterDOMProps(otherProps, { global: true })}
      {...mergeProps(rowProps, hoverProps, focusProps)}
    >
      <div
        className={clsx(
          styles['js-focus'],
          styles['js-hover'],
          styles['sapphire-list__item--interactive'],
          {
            [styles['sapphire-list__item--selected']]:
              state.selectionManager.isSelected(item.key),
            [styles['is-focus']]: isFocusVisible,
            [styles['is-hover']]: isHovered,
          }
        )}
        style={{ ...(sidePadding ? customPaddingStyleProps(sidePadding) : {}) }}
        {...gridCellProps}
      >
        <ListItem.Content
          contentLeft={
            <>
              {state.selectionManager.selectionMode !== 'none' && (
                <SelectionBox item={item} state={state} />
              )}
              {item.props.contentLeft}
            </>
          }
          contentRight={item.props.contentRight}
        />
      </div>
    </div>
  );
});

const SelectionBox = <T,>({
  item,
  state,
}: {
  item: Node<T>;
  state: ListState<T>;
}) => {
  const { checkboxProps } = useGridListSelectionCheckbox(
    { key: item.key },
    state
  );
  if (state.selectionManager.selectionMode === 'none') {
    return null;
  } else if (
    state.selectionManager.selectionMode === 'single' &&
    state.selectionManager.disallowEmptySelection
  ) {
    return (
      <RadioBox
        {...checkboxProps}
        role="checkbox"
        isDisabled={state.disabledKeys.has(item.key)}
      />
    );
  } else {
    return (
      <Checkbox
        {...checkboxProps}
        isDisabled={state.disabledKeys.has(item.key)}
      />
    );
  }
};

const renderDefaultLoadingSkeleton = (index: number) => (
  <ListItem key={index} elementType="div" role="row">
    <ListItem.Content
      contentLeft={
        <ListItemText
          primary={<Skeleton.Text widthType={'randomPx'} index={index} />}
        />
      }
      contentRight={
        <ListItemText
          primary={<Skeleton.Text widthType={'randomPx'} index={index} />}
          secondary={<Skeleton.Text widthType={'randomPx'} index={index} />}
        />
      }
    />
  </ListItem>
);

const customPaddingStyleProps = (sidePadding: number | string) => ({
  paddingLeft:
    typeof sidePadding === 'string' ? sidePadding : `${sidePadding}px`,
  paddingRight:
    typeof sidePadding === 'string' ? sidePadding : `${sidePadding}px`,
});

export const InteractiveList = forwardRef(_InteractiveList) as <
  T extends object
>(
  props: InteractiveListProps<T> & {
    ref?: ForwardedRef<HTMLUListElement>;
  }
) => React.ReactElement;
